import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Calendar, ChevronLeft, ChevronRight, Loader2, MapPin, Users,
  Search, Home, Star, BedDouble, Bath, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WPWidgetEmbedPage = () => {
  const { widgetKey } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [widgetData, setWidgetData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [config, setConfig] = useState({});
  const [widgetType, setWidgetType] = useState('single');
  const [branding, setBranding] = useState({});

  // Search state
  const [searchLocation, setSearchLocation] = useState('');
  const [searchGuests, setSearchGuests] = useState('');
  const [searchCheckIn, setSearchCheckIn] = useState('');
  const [searchCheckOut, setSearchCheckOut] = useState('');
  const [searching, setSearching] = useState(false);

  // Single property booking state
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '', num_guests: 1, special_requests: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const loadWidget = async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/wp/${widgetKey}`);
        if (!resp.ok) throw new Error('Widget not found or inactive');
        const data = await resp.json();
        setWidgetData(data);
        setProperties(data.properties || []);
        setConfig(data.config || {});
        setWidgetType(data.widget_type || 'single');
        setBranding(data.branding || {});
        if (data.widget_type === 'single' && data.properties?.length === 1) {
          setSelectedProperty(data.properties[0]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadWidget();
  }, [widgetKey]);

  // Track click events
  const trackClick = (eventType = 'book_now_click', propertyId = null) => {
    fetch(`${BACKEND_URL}/api/widget-track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ widget_key: widgetKey, event: eventType, property_id: propertyId })
    }).catch(() => {});
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchLocation) params.set('location', searchLocation);
      if (searchCheckIn) params.set('check_in', searchCheckIn);
      if (searchCheckOut) params.set('check_out', searchCheckOut);
      if (searchGuests) params.set('guests', searchGuests);
      const resp = await fetch(`${BACKEND_URL}/api/wp/${widgetKey}/search?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setProperties(data.properties || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedProperty || !checkIn || !checkOut) return;
    setSubmitting(true);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/wp/${widgetKey}/book?property_id=${selectedProperty.property_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...bookingForm, check_in: checkIn, check_out: checkOut })
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Booking failed');
      setBookingResult(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar helpers
  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const formatDateStr = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const isDatePast = (ds) => ds < new Date().toISOString().split('T')[0];

  const primaryColor = config.primary_color || '#FF385C';
  const isDark = config.theme === 'dark';
  const bgClass = isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900';
  const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center p-8">
          <Home className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium mb-2">Widget Not Found</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (bookingResult) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${bgClass}`}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: primaryColor + '20' }}>
            <Star className="w-8 h-8" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Booking Confirmed!</h2>
          <p className="text-gray-500 mb-4">
            Your booking for <strong>{selectedProperty?.name}</strong> from{' '}
            <strong>{bookingResult.booking?.check_in || checkIn}</strong> to{' '}
            <strong>{bookingResult.booking?.check_out || checkOut}</strong> has been received.
          </p>
          <p className="text-sm text-gray-400">Check your email for confirmation details.</p>
        </div>
      </div>
    );
  }

  // Booking panel for a single property
  const BookingPanel = ({ property }) => (
    <div className={`border rounded-xl p-4 ${cardClass}`} data-testid="wp-widget-booking-panel">
      {/* Calendar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} className="text-xs font-medium text-gray-400 py-1">{d}</div>
          ))}
          {Array(firstDayOfMonth(currentMonth)).fill(null).map((_, i) => <div key={`e-${i}`} />)}
          {Array.from({ length: daysInMonth(currentMonth) }, (_, i) => {
            const day = i + 1;
            const dateStr = formatDateStr(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const past = isDatePast(dateStr);
            const isCheckIn = checkIn === dateStr;
            const isCheckOut = checkOut === dateStr;
            const inRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;
            return (
              <button key={day} disabled={past}
                className={`text-xs py-1.5 rounded-lg transition-colors ${
                  past ? 'text-gray-300 cursor-not-allowed' :
                  isCheckIn || isCheckOut ? 'text-white font-bold' :
                  inRange ? 'font-medium' : 'hover:bg-gray-100'
                }`}
                style={{
                  backgroundColor: isCheckIn || isCheckOut ? primaryColor : inRange ? primaryColor + '20' : undefined,
                  color: isCheckIn || isCheckOut ? 'white' : undefined
                }}
                onClick={() => {
                  if (!checkIn || (checkIn && checkOut)) { setCheckIn(dateStr); setCheckOut(null); }
                  else if (dateStr > checkIn) { setCheckOut(dateStr); }
                  else { setCheckIn(dateStr); setCheckOut(null); }
                }}
              >{day}</button>
            );
          })}
        </div>
      </div>
      {checkIn && checkOut && (
        <div className="text-xs text-center mb-3 font-medium" style={{ color: primaryColor }}>
          {checkIn} to {checkOut}
        </div>
      )}
      {/* Guest info */}
      <div className="space-y-2 mb-3">
        <Input placeholder="Your name" value={bookingForm.guest_name} data-testid="wp-guest-name"
          onChange={(e) => setBookingForm(f => ({ ...f, guest_name: e.target.value }))} className="text-sm h-9" />
        <Input placeholder="Email address" value={bookingForm.guest_email} data-testid="wp-guest-email"
          onChange={(e) => setBookingForm(f => ({ ...f, guest_email: e.target.value }))} className="text-sm h-9" />
        <Input placeholder="Phone (optional)" value={bookingForm.guest_phone} data-testid="wp-guest-phone"
          onChange={(e) => setBookingForm(f => ({ ...f, guest_phone: e.target.value }))} className="text-sm h-9" />
      </div>
      <Button className="w-full text-white" style={{ backgroundColor: primaryColor }}
        disabled={!checkIn || !checkOut || !bookingForm.guest_name || !bookingForm.guest_email || submitting}
        onClick={() => { trackClick('book_now_click', property?.property_id); handleBooking(); }} data-testid="wp-book-now-btn">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {submitting ? 'Booking...' : 'Book Now'}
      </Button>
    </div>
  );

  // Property card
  const PropertyCard = ({ property }) => {
    const photo = property.photos?.[0]?.url || property.photos?.[0];
    return (
      <div className={`border rounded-xl overflow-hidden transition-shadow hover:shadow-lg ${cardClass}`}
        data-testid={`wp-property-card-${property.property_id}`}>
        {photo ? (
          <img src={photo} alt={property.name} className="w-full h-48 object-cover" />
        ) : (
          <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
            <Home className="w-10 h-10 text-gray-300" />
          </div>
        )}
        <div className="p-4">
          <h3 className="font-semibold text-base mb-1">{property.name}</h3>
          {property.location && (
            <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
              <MapPin className="w-3 h-3" />
              {property.location.city}{property.location.country ? `, ${property.location.country}` : ''}
            </p>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            {property.bedrooms && <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" />{property.bedrooms} bed</span>}
            {property.bathrooms && <span className="flex items-center gap-1"><Bath className="w-3 h-3" />{property.bathrooms} bath</span>}
            {property.max_guests && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{property.max_guests} guests</span>}
          </div>
          {config.show_prices && property.base_price && (
            <p className="text-lg font-bold mb-3" style={{ color: primaryColor }}>
              {property.currency || 'KSh'}{Number(property.base_price).toLocaleString()}
              <span className="text-xs font-normal text-gray-500"> / night</span>
            </p>
          )}
          <Button className="w-full text-white text-sm" style={{ backgroundColor: primaryColor }}
            onClick={() => { trackClick('click', property.property_id); setSelectedProperty(property); setShowBooking(true); }}
            data-testid={`wp-book-btn-${property.property_id}`}>
            Book Now
          </Button>
        </div>
      </div>
    );
  };

  // ── SINGLE PROPERTY VIEW ──
  if (widgetType === 'single' && selectedProperty && !showBooking) {
    const photo = selectedProperty.photos?.[0]?.url || selectedProperty.photos?.[0];
    return (
      <div className={`min-h-screen p-4 ${bgClass}`} data-testid="wp-widget-single">
        <div className="max-w-md mx-auto">
          {photo && <img src={photo} alt={selectedProperty.name} className="w-full h-56 object-cover rounded-xl mb-4" />}
          <h2 className="text-xl font-bold mb-1">{selectedProperty.name}</h2>
          {selectedProperty.location && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
              <MapPin className="w-3.5 h-3.5" />
              {selectedProperty.location.city}{selectedProperty.location.country ? `, ${selectedProperty.location.country}` : ''}
            </p>
          )}
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
            {selectedProperty.bedrooms && <span>{selectedProperty.bedrooms} bedrooms</span>}
            {selectedProperty.bathrooms && <span>{selectedProperty.bathrooms} bathrooms</span>}
            {selectedProperty.max_guests && <span>Up to {selectedProperty.max_guests} guests</span>}
          </div>
          {config.show_prices && selectedProperty.base_price && (
            <p className="text-2xl font-bold mb-4" style={{ color: primaryColor }}>
              {selectedProperty.currency || 'KSh'}{Number(selectedProperty.base_price).toLocaleString()}
              <span className="text-sm font-normal text-gray-500"> / night</span>
            </p>
          )}
          <BookingPanel property={selectedProperty} />
          {branding.company_name && (
            <p className="text-center text-xs text-gray-400 mt-4">Powered by {branding.company_name}</p>
          )}
        </div>
      </div>
    );
  }

  // ── BOOKING OVERLAY FOR MULTI/SEARCH ──
  if (showBooking && selectedProperty) {
    return (
      <div className={`min-h-screen p-4 ${bgClass}`} data-testid="wp-widget-booking">
        <div className="max-w-md mx-auto">
          <button onClick={() => { setShowBooking(false); setCheckIn(null); setCheckOut(null); }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to properties
          </button>
          <h2 className="text-lg font-bold mb-1">{selectedProperty.name}</h2>
          {config.show_prices && selectedProperty.base_price && (
            <p className="font-bold mb-3" style={{ color: primaryColor }}>
              {selectedProperty.currency || 'KSh'}{Number(selectedProperty.base_price).toLocaleString()} / night
            </p>
          )}
          <BookingPanel property={selectedProperty} />
        </div>
      </div>
    );
  }

  // ── MULTI / SEARCH VIEW ──
  return (
    <div className={`min-h-screen p-4 ${bgClass}`} data-testid="wp-widget-multi">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          {branding.logo_url && <img src={branding.logo_url} alt="" className="h-8 mx-auto mb-2" />}
          <h1 className="text-xl font-bold">{branding.company_name || 'Browse Properties'}</h1>
        </div>

        {/* Search bar for search type */}
        {(widgetType === 'search' || config.show_search) && (
          <div className={`border rounded-xl p-4 mb-6 ${cardClass}`} data-testid="wp-widget-search">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Input placeholder="Location" value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)} className="text-sm h-9" />
              <Input type="date" value={searchCheckIn} placeholder="Check-in"
                onChange={(e) => setSearchCheckIn(e.target.value)} className="text-sm h-9" />
              <Input type="date" value={searchCheckOut} placeholder="Check-out"
                onChange={(e) => setSearchCheckOut(e.target.value)} className="text-sm h-9" />
              <Input type="number" min="1" placeholder="Guests" value={searchGuests}
                onChange={(e) => setSearchGuests(e.target.value)} className="text-sm h-9" />
              <Button className="text-white text-sm h-9" style={{ backgroundColor: primaryColor }}
                onClick={handleSearch} disabled={searching} data-testid="wp-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Search
              </Button>
            </div>
          </div>
        )}

        {/* Properties grid */}
        {properties.length === 0 ? (
          <div className="text-center py-16">
            <Home className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">No properties available</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className={`grid gap-6 ${config.compact_mode ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {properties.map(p => <PropertyCard key={p.property_id} property={p} />)}
          </div>
        )}

        {branding.company_name && (
          <p className="text-center text-xs text-gray-400 mt-8">Powered by {branding.company_name}</p>
        )}
      </div>
    </div>
  );
};

export default WPWidgetEmbedPage;
